import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { apiCall, ApiHttpError, RouteNotFoundError } from "../api/client";
import {
  AuthorityNotice,
  GAOSAFE_APPROVAL_REASON,
  useControlPlaneWritesBlocked,
  useWritesBlocked,
} from "../authority";
import { CONTROL_PLANE_PAYLOAD_DOMAIN } from "../controlPlane/proposal";
import {
  getCurrentChainId,
  getEvmProvider,
  signTypedDataV4,
  WalletProviderUnavailableError,
} from "../auth/wallet";
import { describeProviderError } from "../auth/provider-error";
import { chainLabel } from "../config";

// ─── ANY EVM NETWORK SIGNS AN APPROVAL (the Gao Admin chain law) ─────────
//
// An approval is an OFF-CHAIN EIP-712 signature, not a transaction. The
// authority is the wallet ADDRESS the server recovers — it must be this
// session's GaoSafe signer — and the chain the wallet happens to be on is
// protocol metadata bound into the typed data, exactly as it is in the SIWE
// login message. So the page reads the wallet's network and asks for typed data
// bound to THAT network. It never asks the wallet to switch, never defaults a
// chain, and never compares the wallet's network with the GaoSafe authority
// chain, which the server reads on its own.
//
// This screen used to do the opposite: typed data pinned to the authority chain,
// and a wallet on any other network told to switch to it. That was the
// regression; `ApprovalDetail.test.tsx` pins the law.

/** The wallet moved networks between the read and the prompt — the one way a
 *  network can still stop a signature. Nothing to switch: ask again. */
function networkChangedMessage(active: number | null): string {
  const now = active === null ? "another network" : `${chainLabel(active)} (chainId ${active})`;
  return (
    `The wallet changed network while the approval was being signed — it is now on ${now}. ` +
    "Nothing was signed or submitted. Press Approve again: any EVM network can sign this approval."
  );
}

/** No EIP-1193 provider at all. */
const NO_EVM_WALLET =
  "No EVM wallet is connected, and this approval is an EVM (EIP-712) signature. " +
  "Nothing was signed or submitted. Connect MetaMask, Rabby or Frame — on any network — and press Approve again.";

/** A provider that does not report an EVM network: not a wallet this signature can come from. */
const NOT_AN_EVM_WALLET =
  "The connected wallet did not report an EVM network, and this approval is an EVM (EIP-712) signature. " +
  "Nothing was signed or submitted. Use an EVM wallet (MetaMask, Rabby, Frame) — on any network.";

// Approval detail — session-auth BFF flow (PR #135 BE).
//
// Loads `GET /v2/admin/bff/approvals/:id`, surfaces the proposal +
// approvals[], and offers an Approve button for the current owner
// (when the proposal is still in `proposed` state and this session
// has not already decided). Approve clicks fetch the EIP-712
// typed-data from `GET /:id/typed-data`, ask the wallet to sign
// via `eth_signTypedData_v4`, and POST the signature to
// `/:id/approve`. Owner identity + wallet are derived from the
// session — body fields ARE forwarded to the BE as sanity hints
// but the BE only honours the session-derived values.

interface BffProposalView {
  id: string;
  actionType: string;
  actionDigest: string;
  payloadHash: string;
  thresholdRequired: number;
  status: "proposed" | "approved" | "rejected" | "expired" | "executed" | "cancelled";
  proposedByOwnerId: string;
  expiresAt: string;
  createdAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  executedAt: string | null;
  cancelledAt: string | null;
  approveCount: number;
  rejectCount: number;
  /** Populated by migration 0044 + BE PR #136 when the proposal's
   *  authorised execute call broadcasts successfully. */
  executedTxHash: string | null;
  executedBlockNumber: number | null;
  executedByWalletAddress: string | null;
}

interface ApprovalView {
  id: string;
  ownerIdentityId: string;
  walletAddress: string;
  decision: "approve" | "reject";
  signedAt: string;
  createdAt: string;
}

interface BffApprovalDetailResponse {
  ok: boolean;
  proposal: BffProposalView;
  payload: unknown;
  approvals: ApprovalView[];
  thresholdMet: boolean;
  vetoed: boolean;
  sessionOwnerIdentityId: string;
  sessionWalletAddress: string;
  sessionHasDecided: boolean;
  sessionOwnerDecision: string | null;
  requestId: string;
}

interface BffTypedDataResponse {
  ok: boolean;
  chainId: number;
  requestId: string;
  typedData: unknown;
}

interface BffApproveResponse {
  ok: boolean;
  proposal: BffProposalView;
  decision: "approve";
  approvalId: string;
  transitionedTo: string | null;
  thresholdMet: boolean;
  vetoed: boolean;
  sessionOwnerIdentityId: string;
  sessionWalletAddress: string;
  requestId: string;
}

function ApprovalDetailInner() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<BffApprovalDetailResponse | null>(null);
  /** Is this proposal one of the Product Control Plane's governed acts?
   *  Positive identification only — an unreadable or foreign payload is not. */
  const isControlPlaneProposal =
    data !== null &&
    typeof data.payload === "object" &&
    data.payload !== null &&
    (data.payload as Record<string, unknown>)["domain"] === CONTROL_PLANE_PAYLOAD_DOMAIN;
  // Hooks, so they must sit with the other hooks — above every early return.
  //
  // THIS SCREEN APPROVES PROPOSALS FROM EVERY DOMAIN. Escrow, settlement and
  // treasury proposals land here alongside Control Plane ones, so a single
  // authority would be wrong in one direction or the other: gating the screen
  // on the Control Plane's capability would arm approval of a treasury
  // proposal the moment Control Plane writes opened. The authority therefore
  // follows the PROPOSAL, not the screen — and anything whose domain this
  // console cannot positively identify as the Control Plane's falls back to
  // the global gate, which is closed.
  const controlPlaneWritesBlocked = useControlPlaneWritesBlocked();
  const globalWritesBlocked = useWritesBlocked();
  const writesBlocked = isControlPlaneProposal
    ? controlPlaneWritesBlocked
    : globalWritesBlocked;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [signing, setSigning] = useState<boolean>(false);
  const [signNote, setSignNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    setLoading(true);
    try {
      const j = await apiCall<BffApprovalDetailResponse>(
        `/v2/admin/bff/approvals/${encodeURIComponent(id)}`,
      );
      setData(j);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError("Route not registered on BE.");
      } else if (e instanceof ApiHttpError) {
        setError(`HTTP ${e.status}.`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onApprove() {
    if (!id || !data) return;
    setError(null);
    setSignNote(null);
    setSigning(true);
    try {
      // 1. The wallet's own network — read, never chosen. An EVM wallet
      //    reports one; anything that does not is not an EVM wallet, and this
      //    is an EVM signature. Nothing has been fetched, signed or sent yet.
      const provider = getEvmProvider();
      let chainId: number;
      try {
        chainId = await getCurrentChainId(provider);
      } catch {
        setError(NOT_AN_EVM_WALLET);
        setSignNote(null);
        return;
      }

      // 2. Typed data bound to that network. The server generates the
      //    requestId and embeds it inside `typedData.message.requestId`;
      //    we must round-trip the same value to POST /approve. The server
      //    must bind exactly the network asked for — a different one would
      //    be refused by the wallet or recover another address — so an echo
      //    that disagrees stops here, before the wallet is asked.
      setSignNote(`Fetching EIP-712 typed-data for ${chainLabel(chainId)} (chainId ${chainId})…`);
      const td = await apiCall<BffTypedDataResponse>(
        `/v2/admin/bff/approvals/${encodeURIComponent(id)}/typed-data?chainId=${chainId}`,
      );
      if (!td.ok) throw new Error("typed-data response not ok");
      const requestId = td.requestId;
      const boundTo = (td.typedData as { domain?: { chainId?: unknown } } | null)?.domain?.chainId;
      if (td.chainId !== chainId || Number(boundTo) !== chainId) {
        throw new Error(
          `The server bound this approval to chainId ${String(td.chainId)}, not the wallet's ${chainId}. Nothing was signed.`,
        );
      }

      // 3. Ask the wallet to sign. The wallet provider validates
      //    that the connected account matches the session's
      //    wallet (the user picks the right account in MetaMask /
      //    Frame / Rabby); the server then refuses any signer that is
      //    not this session's.
      setSignNote("Requesting wallet signature…");
      const signature = await signTypedDataV4(
        provider,
        td.typedData,
        data.sessionWalletAddress,
      );
      if (typeof signature !== "string" || !signature.startsWith("0x")) {
        throw new Error("Wallet returned a malformed signature.");
      }

      // 4. POST to the approve endpoint, naming the network the wallet
      //    signed on. Body carries walletAddress only as sanity hint; BE
      //    re-derives both wallet and owner from the session and rejects
      //    mismatch with 400.
      setSignNote("Submitting approval to BE…");
      const out = await apiCall<BffApproveResponse>(
        `/v2/admin/bff/approvals/${encodeURIComponent(id)}/approve`,
        {
          method: "POST",
          body: {
            chainId,
            signature,
            requestId,
            walletAddress: data.sessionWalletAddress,
          },
        },
      );
      setSignNote(
        `Approval recorded. approveCount=${out.proposal.approveCount}/${out.proposal.thresholdRequired}, status=${out.proposal.status}.`,
      );
      // 5. Reload detail so the approvals[] + counts refresh.
      await load();
    } catch (e) {
      // A wallet rejects with a PLAIN OBJECT, not an Error — an `instanceof
      // Error` chain therefore reached its fallback and printed "Unknown
      // error." for every wallet refusal, including the one that has a
      // one-line fix. Provider rejections are read, never stringified, and
      // never reproduced raw: `describeProviderError` keeps the code and the
      // message and describes `data` rather than copying it.
      const provider = describeProviderError(e);
      if (provider !== null) {
        setError(
          provider.kind === "wrong_chain"
            ? networkChangedMessage(provider.activeChainId)
            : provider.summary,
        );
      } else if (e instanceof WalletProviderUnavailableError) {
        setError(NO_EVM_WALLET);
      } else if (e instanceof ApiHttpError) {
        const bodyMsg =
          e.body !== null && typeof e.body === "object" && "message" in e.body
            ? String((e.body as { message: unknown }).message)
            : "";
        setError(`HTTP ${e.status}${bodyMsg ? ` — ${bodyMsg}` : ""}.`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("The wallet or the network returned no usable detail. Nothing was submitted.");
      }
      setSignNote(null);
    } finally {
      setSigning(false);
    }
  }

  if (loading && !data) {
    return <p style={{ padding: 12, color: "var(--fg-dim)" }}>Loading…</p>;
  }

  if (error && !data) {
    return (
      <div style={{ padding: 12, color: "#d23f3f" }}>
        <p>{error}</p>
        <Link to="/approvals" style={{ color: "var(--accent, #2c7be5)" }}>
          ← Back to list
        </Link>
      </div>
    );
  }

  if (!data) return null;

  const p = data.proposal;
  const canApprove =
    p.status === "proposed" &&
    !data.sessionHasDecided &&
    !data.vetoed &&
    new Date(p.expiresAt).getTime() > Date.now();

  return (
    <div>
      <p style={{ margin: "4px 0", fontSize: 12 }}>
        <Link to="/approvals" style={{ color: "var(--accent, #2c7be5)" }}>
          ← All approvals
        </Link>
      </p>

      <h3 style={{ fontSize: 14, fontFamily: "monospace", margin: "12px 0 4px 0" }}>{p.id}</h3>
      <p style={{ fontSize: 12, color: "var(--fg-dim)", margin: "4px 0" }}>
        Action: <strong>{p.actionType}</strong> · Status: <strong>{p.status}</strong> · Threshold:{" "}
        <strong>
          {p.approveCount}/{p.thresholdRequired} approves
        </strong>{" "}
        ({p.rejectCount} rejects)
      </p>
      <p style={{ fontSize: 12, color: "var(--fg-dim)", margin: "4px 0" }}>
        Proposed by <code>{p.proposedByOwnerId}</code> · expires <code>{p.expiresAt}</code>
      </p>

      <section style={{ margin: "16px 0", padding: 12, border: "1px solid var(--border, #ccc)" }}>
        <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>Payload (redacted, display-only)</h4>
        <pre
          style={{
            background: "var(--bg-faint, #fafafa)",
            padding: 8,
            fontSize: 11,
            overflow: "auto",
            maxHeight: 260,
            margin: 0,
          }}
        >
          {JSON.stringify(data.payload, null, 2)}
        </pre>
        <p style={{ fontSize: 11, color: "var(--fg-dim)", margin: "8px 0 0 0" }}>
          actionDigest: <code>{p.actionDigest}</code>
          <br />
          payloadHash: <code>{p.payloadHash}</code>
        </p>
      </section>

      <section style={{ margin: "16px 0" }}>
        <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>
          Owner decisions ({data.approvals.length})
        </h4>
        {data.approvals.length === 0 ? (
          <p style={{ fontSize: 12, color: "var(--fg-dim)" }}>None yet.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border, #ccc)", textAlign: "left" }}>
                <th style={{ padding: "6px 4px" }}>Owner</th>
                <th style={{ padding: "6px 4px" }}>Wallet</th>
                <th style={{ padding: "6px 4px" }}>Decision</th>
                <th style={{ padding: "6px 4px" }}>Signed at</th>
              </tr>
            </thead>
            <tbody>
              {data.approvals.map((a) => (
                <tr key={a.id} style={{ borderBottom: "1px solid var(--border-faint, #eee)" }}>
                  <td style={{ padding: "6px 4px", fontFamily: "monospace" }}>
                    {a.ownerIdentityId}
                  </td>
                  <td style={{ padding: "6px 4px", fontFamily: "monospace" }}>
                    {a.walletAddress}
                  </td>
                  <td
                    style={{
                      padding: "6px 4px",
                      color: a.decision === "approve" ? "#0a9444" : "#d23f3f",
                      fontWeight: 600,
                    }}
                  >
                    {a.decision}
                  </td>
                  <td style={{ padding: "6px 4px", fontFamily: "monospace", fontSize: 11 }}>
                    {a.signedAt}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {p.executedAt !== null && (
        <section
          style={{
            margin: "16px 0",
            padding: 12,
            border: "1px solid #0a9444",
            background: "rgba(10, 148, 68, 0.04)",
          }}
        >
          <h4 style={{ fontSize: 13, margin: "0 0 8px 0", color: "#0a9444" }}>
            ✓ Executed on-chain
          </h4>
          <p style={{ fontSize: 12, margin: "4px 0" }}>
            Executed at <code>{p.executedAt}</code>
          </p>
          {p.executedTxHash && (
            <p style={{ fontSize: 12, margin: "4px 0", wordBreak: "break-all" }}>
              Tx hash:{" "}
              <code>{p.executedTxHash}</code>{" "}
              <a
                href={`https://sepolia.basescan.org/tx/${p.executedTxHash}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--accent, #2c7be5)", marginLeft: 4 }}
              >
                BaseScan ↗
              </a>
            </p>
          )}
          {p.executedBlockNumber !== null && (
            <p style={{ fontSize: 12, margin: "4px 0" }}>
              Block: <code>{p.executedBlockNumber}</code>
            </p>
          )}
          {p.executedByWalletAddress && (
            <p style={{ fontSize: 12, margin: "4px 0" }}>
              Initiated by session wallet:{" "}
              <code>{p.executedByWalletAddress}</code>{" "}
              <span style={{ color: "var(--fg-dim)", fontSize: 11 }}>
                (the on-chain `from` is the V3 owner key held by the signer
                service, not this wallet)
              </span>
            </p>
          )}
        </section>
      )}

      <section style={{ margin: "16px 0", padding: 12, border: "1px solid var(--border, #ccc)" }}>
        <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>This session</h4>
        <p style={{ fontSize: 12, margin: "4px 0" }}>
          Owner <code>{data.sessionOwnerIdentityId}</code> · wallet{" "}
          <code>{data.sessionWalletAddress}</code>
        </p>
        {data.sessionHasDecided ? (
          <p style={{ fontSize: 12, color: "var(--fg-dim)", margin: "4px 0" }}>
            You have already <strong>{data.sessionOwnerDecision ?? "decided"}</strong> on this
            proposal.
          </p>
        ) : data.vetoed ? (
          <p style={{ fontSize: 12, color: "#d23f3f", margin: "4px 0" }}>
            Proposal vetoed — cannot record further decisions.
          </p>
        ) : p.status !== "proposed" ? (
          <p style={{ fontSize: 12, color: "var(--fg-dim)", margin: "4px 0" }}>
            Proposal is no longer in <code>proposed</code> state.
          </p>
        ) : null}

        {canApprove && writesBlocked && (
          <div style={{ marginTop: 8 }}>
            <AuthorityNotice scope="Approving is blocked." />
          </div>
        )}
        {canApprove && (
          <button
            type="button"
            onClick={writesBlocked ? undefined : () => void onApprove()}
            disabled={writesBlocked || signing}
            title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
            style={{
              marginTop: 8,
              padding: "6px 14px",
              fontSize: 13,
              background: "var(--accent, #2c7be5)",
              color: "white",
              border: "none",
              borderRadius: 3,
              cursor: signing ? "default" : "pointer",
            }}
          >
            {signing ? "Signing…" : "Approve (wallet sign + POST)"}
          </button>
        )}
        {signNote && (
          <p style={{ fontSize: 12, color: "var(--fg-dim)", margin: "8px 0 0 0" }}>{signNote}</p>
        )}
        {error && (
          <p role="alert" style={{ fontSize: 12, color: "#d23f3f", margin: "8px 0 0 0" }}>
            {error}
          </p>
        )}
      </section>
    </div>
  );
}

export function ApprovalDetail() {
  return (
    <div>
      <PageHeader
        title="Approval detail"
        subtitle="Session-auth BFF. Wallet-signed EIP-712 approval. Browser does NOT carry HMAC."
        method="GET / POST"
        path="/v2/admin/bff/approvals/:id"
      />
      <AuthRequired>
        <ApprovalDetailInner />
      </AuthRequired>
    </div>
  );
}
